import * as pdfjsLib from "pdfjs-dist";

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

/**
 * Check if a PDF file is password-protected
 * @param file - The PDF file to check
 * @returns Promise<boolean> - true if password-protected, false otherwise
 */
export const isPdfPasswordProtected = async (file: File): Promise<boolean> => {
  try {
    // Only check PDF files
    if (file.type !== "application/pdf") {
      return false;
    }

    // Read file as ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();

    // Try to load the PDF
    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });

    try {
      await loadingTask.promise;
      // If we can load it without error, it's not password-protected
      return false;
    } catch (error: any) {
      // Check if the error is a password error
      if (error.name === "PasswordException") {
        return true;
      }
      // Other errors (corrupted PDF, etc.) - re-throw
      throw error;
    }
  } catch (error) {
    console.error("Error checking PDF password protection:", error);
    throw error;
  }
};

/**
 * Attempt to validate a PDF with a password
 * @param file - The PDF file
 * @param password - The password to try
 * @returns Promise<boolean> - true if password is correct, false otherwise
 */
export const validatePdfPassword = async (
  file: File,
  password: string
): Promise<boolean> => {
  try {
    const arrayBuffer = await file.arrayBuffer();

    const loadingTask = pdfjsLib.getDocument({
      data: arrayBuffer,
      password: password,
    });

    try {
      const pdf = await loadingTask.promise;
      // If we can load it, the password is correct
      // Try to access a page to make sure it's fully accessible
      await pdf.getPage(1);
      return true;
    } catch (error: any) {
      if (error.name === "PasswordException") {
        // Wrong password
        return false;
      }
      // Other errors - re-throw
      throw error;
    }
  } catch (error) {
    console.error("Error validating PDF password:", error);
    throw error;
  }
};
